import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTonWallet, useTonConnectUI } from "@tonconnect/ui-react";
import { api, type User, type Challenge } from "../lib/api";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";

function Join() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();

  const [user, setUser] = useState<User | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  // Side is no longer user-selectable - opponent always gets opposite of creator
  const [stake, setStake] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinedSuccessfully, setJoinedSuccessfully] = useState(false);
  const [joinedStake, setJoinedStake] = useState<number>(0);
  const [joinedSideLabel, setJoinedSideLabel] = useState<string>("");

  // Get current user's telegram ID
  const currentUserTelegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString();

  // Check if user is the creator
  const isCreator = challenge?.creator?.telegramId === currentUserTelegramId;

  // Check if user has already joined
  const existingPosition = challenge?.positions?.find(
    (p) => p.user?.telegramId === currentUserTelegramId
  );
  const hasAlreadyJoined = !!existingPosition;

  // Determine creator's side and opponent's side (opposite)
  const creatorPosition = challenge?.positions?.find(
    (p) => p.user?.telegramId === challenge?.creator?.telegramId
  );
  const creatorSide = creatorPosition?.side || "YES";
  const opponentSide: "YES" | "NO" = creatorSide === "YES" ? "NO" : "YES";

  // Get labels for display
  const opponentSideLabel = opponentSide === "YES"
    ? (challenge?.labelA || "YES")
    : (challenge?.labelB || "NO");

  // Check if challenge has a timer
  const hasTimer = !!(challenge?.durationMinutes || challenge?.resolutionTime);

  // Get timer info text
  const getTimerInfoText = () => {
    if (!challenge) return null;
    if (challenge.durationMinutes) {
      const mins = challenge.durationMinutes;
      if (mins >= 60) {
        const hours = mins / 60;
        return `${hours}h timer starts when you join`;
      }
      return `${mins}m timer starts when you join`;
    }
    if (challenge.resolutionTime) {
      const date = new Date(challenge.resolutionTime);
      return `Resolution at ${date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
    }
    return null;
  };

  // Get timer info for locked challenge (after joining)
  const getLockedTimerText = () => {
    if (!challenge?.timerEndsAt) return null;
    const timerEndsAt = new Date(challenge.timerEndsAt);
    const now = new Date();
    const diff = timerEndsAt.getTime() - now.getTime();
    if (diff <= 0) return "Ready to resolve!";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m until resolution`;
    return `${minutes}m until resolution`;
  };

  // Check if challenge already has an opponent (all challenges are 1v1)
  const hasOpponent = challenge?.positions?.some(
    (p) => p.side === opponentSide
  );

  // Use Telegram's native back button (navigates back in history)
  useTelegramBackButton();

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch user and challenge in parallel
        const [userData, challengeData] = await Promise.all([
          api.getMe(),
          challengeId ? api.getChallenge(challengeId) : Promise.reject("No challenge ID"),
        ]);

        setUser(userData.user);
        setDemoMode(userData.demoMode);
        setChallenge(challengeData.challenge);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load challenge");
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [challengeId]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!challengeId) {
      setError("No challenge ID");
      return;
    }

    // In real mode, require wallet connection
    if (!demoMode && !wallet) {
      await tonConnectUI.openModal();
      return;
    }

    if (!stake) {
      setError("Please enter a stake amount");
      return;
    }

    const stakeAmount = parseFloat(stake);
    if (user && stakeAmount > user.balance) {
      setError(`Insufficient balance. You have ${user.balance.toFixed(2)} TON`);
      return;
    }

    setIsSubmitting(true);

    try {
      // Side is auto-assigned by backend (opposite of creator)
      const result = await api.joinChallenge(challengeId, {
        stake: stakeAmount,
      });

      // Update local state
      setChallenge(result.challenge);
      if (result.userBalance !== null) {
        setUser((prev) => prev ? { ...prev, balance: result.userBalance! } : null);
      }

      // Show in-app success screen
      setJoinedStake(stakeAmount);
      setJoinedSideLabel(opponentSideLabel);
      setJoinedSuccessfully(true);
    } catch (err) {
      console.error("Failed to join challenge:", err);
      setError(err instanceof Error ? err.message : "Failed to join challenge");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="page loading">Loading...</div>;
  }

  // Success screen after joining
  if (joinedSuccessfully && challenge) {
    return (
      <div className="page join success-screen">
        <div className="success-icon">&#x2705;</div>
        <h1>You're In!</h1>

        <div className="challenge-preview-minimal">
          <p className="preview-description">"{challenge.description}"</p>
          <div className="preview-amount">
            <span className="amount-value">{joinedStake}</span>
            <span className="amount-currency">TON</span>
          </div>
          <p className="preview-side">You're backing <strong>{joinedSideLabel}</strong></p>
          {challenge.timerEndsAt && (
            <div className="timer-info-badge">
              <span className="timer-icon">⏱️</span>
              <span>{getLockedTimerText()}</span>
            </div>
          )}
        </div>

        <p className="share-prompt">
          {challenge.timerEndsAt
            ? "Challenge is now locked. Wait for the timer to resolve!"
            : "Challenge is now locked. Wait for the outcome!"}
        </p>

        <div className="success-actions">
          <button
            className="btn primary full-width"
            onClick={() => navigate(`/challenge/${challengeId}`)}
          >
            View Challenge
          </button>
          <button
            className="btn secondary full-width"
            onClick={() => navigate("/")}
          >
            Go Home
          </button>
        </div>

        {user && (
          <p className="balance-note">
            New balance: {user.balance.toFixed(2)} TON
          </p>
        )}
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="page error">
        <h1>Challenge Not Found</h1>
        <p>{error || "This challenge doesn't exist or has been removed."}</p>
        <button className="btn primary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  if (challenge.status !== "OPEN") {
    return (
      <div className="page error">
        <h1>Challenge Closed</h1>
        <p>This challenge is no longer accepting new participants.</p>
        <p>Status: {challenge.status}</p>
        <button className="btn primary" onClick={() => navigate(`/challenge/${challengeId}`)}>
          View Challenge
        </button>
        <button className="btn secondary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // User is the creator - redirect to challenge detail
  if (isCreator) {
    return (
      <div className="page error">
        <h1>Your Challenge</h1>
        <p>You created this challenge. Share it with a friend to get an opponent!</p>
        <button className="btn primary" onClick={() => navigate(`/challenge/${challengeId}`)}>
          View Challenge
        </button>
        <button className="btn secondary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // User already joined
  if (hasAlreadyJoined) {
    return (
      <div className="page error">
        <h1>Already Joined</h1>
        <p>You've already joined this challenge on the {existingPosition?.side === "YES" ? (challenge.labelA || "YES") : (challenge.labelB || "NO")} side.</p>
        <button className="btn primary" onClick={() => navigate(`/challenge/${challengeId}`)}>
          View Challenge
        </button>
        <button className="btn secondary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  // Challenge already has an opponent (all challenges are 1v1)
  if (hasOpponent) {
    return (
      <div className="page error">
        <h1>Challenge Full</h1>
        <p>This challenge already has an opponent.</p>
        <button className="btn primary" onClick={() => navigate(`/challenge/${challengeId}`)}>
          View Challenge
        </button>
        <button className="btn secondary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="page join">
      <h1>Join Challenge</h1>

      {error && <div className="error-banner">{error}</div>}

      <div className="challenge-card">
        <p className="description">"{challenge.description}"</p>
        <div className="pool-info">
          <span>Pool: {parseFloat(challenge.poolTotal).toFixed(2)} TON</span>
          <span>Status: {challenge.status}</span>
        </div>
        <div className="sides-info">
          <div className="side-info yes">
            <span className="label">{challenge.labelA || "YES"}</span>
            <span className="total">{challenge.yesSide.total.toFixed(2)} TON</span>
            <span className="count">{challenge.yesSide.participants.length} participant(s)</span>
          </div>
          <div className="side-info no">
            <span className="label">{challenge.labelB || "NO"}</span>
            <span className="total">{challenge.noSide.total.toFixed(2)} TON</span>
            <span className="count">{challenge.noSide.participants.length} participant(s)</span>
          </div>
        </div>
        {hasTimer && (
          <div className="timer-info-badge">
            <span className="timer-icon">⏱️</span>
            <span>{getTimerInfoText()}</span>
          </div>
        )}
      </div>

      <form onSubmit={handleJoin}>
        {/* Show which side opponent will be on (auto-assigned) */}
        <div className="form-group">
          <div className="versus-info">
            <span className="versus-icon">⚔️</span>
            <p>You'll be backing <strong>{opponentSideLabel}</strong></p>
            <span className="hint">Opposite of creator's side</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="stake">Your contribution (TON)</label>
          <input
            id="stake"
            type="number"
            placeholder="10"
            min="0.1"
            step="0.1"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            required
          />
          {user && (
            <span className="hint">Balance: {user.balance.toFixed(2)} TON</span>
          )}
        </div>

        <button type="submit" className="btn primary full-width" disabled={isSubmitting}>
          {isSubmitting ? "Joining..." : "Accept Challenge"}
        </button>
      </form>
    </div>
  );
}

export default Join;
